<template>
  <div class="pointOverviewList">
    <LoadingPlaceholder v-if="loading" />
    <template v-else>
      <div
        v-if="orphanedLiftPointsCount > 0"
        class="alert alert-warning"
      >
        {{ `${orphanedLiftPointsCount} point(s) have external IDs but are not connected to any section.` }}
      </div>
      <Grid
        :ref="kgm_ref"
        :style="{height: 'auto'}"
        :data-items="kgm_computedGridItems(points)"
        :columns="kgm_responsiveColumns()"
        :filterable="false"
        :filter="kgm_filter"
        :pageable="kgm_pagable"
        :page-size="kgm_take"
        :skip="kgm_skip"
        :take="kgm_take"
        :total="kgm_allGridItems(points)"
        :sortable="{
          allowUnsort: true,
          mode: kgm_sortMode
        }"
        :sort="kgm_sort"
        selected-field="selected"
        @rowclick="kgm_gridOnRowClick"
        @filterchange="kgm_gridFilterChange"
        @pagechange="kgm_gridPageChange"
        @sortchange="kgm_gridSortChange"
      >
        <template
          slot="booleanTemplate"
          slot-scope="{props}"
        >
          <td :class="props.className">
            <input
              :checked="kgm_getNestedValue(props.field, props.dataItem)"
              type="checkbox"
              disabled
              readonly
            >
          </td>
        </template>
        <template
          slot="externalIdsCell"
          slot-scope="{props}"
        >
          <td :class="props.className">
            {{ props.dataItem.externalIdsText }}
          </td>
        </template>
        <template
          slot="locationCell"
          slot-scope="{props}"
        >
          <td :class="props.className">
            <template v-if="props.dataItem.locationInfo">
              <div>{{ `Location ${props.dataItem.locationInfo.id}` }}</div>
              <small class="text-muted">
                {{ `Resort ${props.dataItem.locationInfo.resortId || resortId}` }}
              </small>
            </template>
            <span
              v-else
              class="text-muted"
            >No location</span>
          </td>
        </template>
        <template
          slot="sectionsCell"
          slot-scope="{props}"
        >
          <td :class="props.className">
            <template v-if="props.dataItem.sectionConnections.length > 0">
              <div class="pointOverviewList__sectionCell">
                <span>{{ props.dataItem.sectionConnections.length }}</span>
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm ml-2"
                  title="Show sections"
                  @click.stop="openSectionsModal(props.dataItem)"
                >
                  <font-awesome-icon icon="eye" />
                </button>
              </div>
            </template>
            <span
              v-else
              class="text-muted"
            >No section</span>
          </td>
        </template>
        <template
          slot="optionsTemplate"
          slot-scope="{props}"
        >
          <td :class="props.className">
            <div class="btn-group">
              <button
                class="btn btn-primary btn-sm"
                @click="showSidebarEdit(props.dataItem)"
              >
                <font-awesome-icon
                  class="mr-2"
                  icon="edit"
                />
                <span>{{ $t('edit') }}</span>
              </button>
              <button
                class="btn btn-danger btn-sm ml-1"
                @click="openConfirmModal(props.dataItem)"
              >
                <font-awesome-icon
                  class="mr-2"
                  icon="trash"
                />
              </button>
            </div>
          </td>
        </template>
      </Grid>
      <Sidebar
        v-if="showSidebar"
        :show-sidebar="showSidebar"
        @close="close"
      >
        <ResortPointAdd
          v-if="addingElement"
          :resort-id="resortId"
          @reload="reloadPoints"
        />
        <PhotopointEdit
          v-else-if="selectedPoint && isPhotopoint(selectedPoint)"
          :resort-id="resortId"
          :photopoint="selectedPoint"
          @reload="reloadPoints"
        />
        <ResortPointEdit
          v-else-if="selectedPoint"
          :resort-id="resortId"
          :point="selectedPoint"
          @reload="reloadPoints"
        />
      </Sidebar>
    </template>
    <SweetModal
      ref="confirmDelete"
      :title="$t('introOutroListComp.deleteElement')"
      icon="warning"
    >
      <p v-if="selectedPoint">
        {{ $t('sureToDelete', {'0' : selectedPoint.id }) }}
      </p>
      <button
        slot="button"
        class="btn btn-secondary float-left mb-3"
        @click="$refs.confirmDelete.close()"
      >
        <font-awesome-icon
          class="mr-2"
          icon="times"
        />{{ $t('cancel') }}
      </button>
      <button
        slot="button"
        class="btn btn-danger float-right mb-3"
        @click="deletePoint()"
      >
        <font-awesome-icon
          class="mr-2"
          icon="trash"
        />{{ $t('delete') }}
      </button>
      <div class="clearfix" />
    </SweetModal>
    <SweetModal
      ref="sectionsModal"
      :title="sectionsModalTitle"
      width="800"
    >
      <div v-if="selectedSectionsPoint">
        <div class="mb-3">
          <strong>{{ selectedSectionsPoint.name || selectedSectionsPoint.key || `Point ${selectedSectionsPoint.id}` }}</strong>
          <div class="text-muted">
            {{ `${selectedSectionsPoint.sectionConnections.length} section connection(s)` }}
          </div>
        </div>
        <div
          v-for="sectionConnection in selectedSectionsPoint.sectionConnections"
          :key="`section-modal-${selectedSectionsPoint.id}-${sectionConnection.id}-${sectionConnection.role}`"
          class="pointOverviewList__sectionItem"
        >
          <div class="font-weight-bold">
            {{ `${sectionConnection.id} - ${sectionConnection.name}` }}
          </div>
          <div class="text-muted">
            {{ sectionConnection.typeName || "Section" }}
          </div>
          <div class="text-muted">
            {{ sectionConnection.role }}
          </div>
        </div>
      </div>
      <button
        slot="button"
        type="button"
        class="btn btn-secondary float-right mb-3"
        @click="$refs.sectionsModal.close()"
      >
        <font-awesome-icon
          class="mr-2"
          icon="times"
        />{{ $t('close') }}
      </button>
      <div class="clearfix" />
    </SweetModal>
  </div>
</template>

<script>
import { kendoGridMixin } from "@/mixins/kendoGridMixin.js";
import Sidebar from "@/components/Base/Sidebar.vue";
import PhotopointEdit from "@/components/Resort/PhotopointEdit.vue";
import ResortPointAdd from "@/components/Resort/ResortPointAdd.vue";
import ResortPointEdit from "@/components/Resort/ResortPointEdit.vue";
import { SweetModal } from "sweet-modal-vue";

export default {
  name: "PointOverviewList",
  components: {
    Sidebar,
    PhotopointEdit,
    ResortPointAdd,
    ResortPointEdit,
    SweetModal,
  },
  mixins: [kendoGridMixin],
  props: {
    resortId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      points: [],
      selectedPoint: null,
      selectedSectionsPoint: null,
      showSidebar: false,
      addingElement: false,
      kgm_columns: [
        {
          field: "id",
          filterable: false,
          title: "Id",
          width: "80px",
        },
        {
          field: "sourceLabel",
          filterable: false,
          title: "Kind",
          width: "130px",
        },
        {
          field: "key",
          filterable: false,
          title: "Key",
          hideOn: ["xsDown"],
        },
        {
          field: "name",
          filterable: false,
          title: "Name",
        },
        {
          field: "typeLabel",
          filterable: false,
          title: "Type",
          hideOn: ["smDown"],
          width: "180px",
        },
        {
          field: "externalIdsText",
          filterable: false,
          title: "External IDs",
          cell: "externalIdsCell",
          hideOn: ["smDown"],
        },
        {
          field: "locationSummary",
          filterable: false,
          title: "Location",
          cell: "locationCell",
          hideOn: ["smDown"],
          width: "150px",
        },
        {
          field: "sectionsSummary",
          filterable: false,
          title: "Sections",
          cell: "sectionsCell",
          hideOn: ["mdDown"],
        },
        {
          field: "visible",
          filterable: false,
          title: "Visible",
          cell: "booleanTemplate",
          width: "85px",
        },
        {
          field: "active",
          filterable: false,
          title: "Active",
          cell: "booleanTemplate",
          width: "85px",
        },
        {
          filterable: false,
          title: "Options",
          cell: "optionsTemplate",
          width: "165px",
        },
      ],
    };
  },
  computed: {
    orphanedLiftPointsCount() {
      return (this.points || []).filter(
        (point) =>
          point.typeId !== 21 &&
          (point.typeId === 1 || point.typeId === 2) &&
          point.externalIds.length > 0 &&
          point.sectionConnections.length === 0
      ).length;
    },
    sectionsModalTitle() {
      if (!this.selectedSectionsPoint) {
        return "Sections";
      }

      return `Sections for Point ${this.selectedSectionsPoint.id}`;
    },
  },
  watch: {
    resortId() {
      this.getData();
    },
  },
  created() {
    this.getData();
  },
  methods: {
    kgm_getNestedValue(field, dataItem) {
      const path = field.split(".");
      let data = dataItem;
      path.forEach((p) => {
        data = data ? data[p] : undefined;
      });
      return data;
    },
    isPhotopoint(point) {
      return Number(point.typeId) === 21 || point.deleteType === "Photopoint";
    },
    unique(items) {
      return Array.from(new Set(items.filter((item) => item != null)));
    },
    clone(item) {
      return JSON.parse(JSON.stringify(item));
    },
    openConfirmModal(point) {
      this.selectedPoint = this.clone(point);
      this.$refs.confirmDelete.open();
    },
    openSectionsModal(point) {
      this.selectedSectionsPoint = this.clone(point);
      this.$refs.sectionsModal.open();
    },
    show() {
      this.showSidebar = true;
    },
    close() {
      this.showSidebar = false;
    },
    showSidebarAdd() {
      this.selectedPoint = null;
      this.addingElement = true;
      this.show();
    },
    showSidebarEdit(point) {
      this.selectedPoint = this.clone(point);
      this.addingElement = false;
      this.show();
    },
    async reloadPoints() {
      this.showSidebar = false;
      this.addingElement = false;
      this.$emit("reloadAuditLogs");
      await this.getData();
    },
    async getData() {
      this.loading = true;
      try {
        const response = await this.axios.get(
          `/Resort/GetResortPointOverview?resortId=${this.resortId}&includeSharedResorts=true`
        );
        this.points = response.data;
      } finally {
        this.loading = false;
      }
    },
    async deletePoint() {
      if (!this.selectedPoint) {
        return;
      }

      this.loading = true;
      await this.axios
        .delete(`/Resort/RemovePoint?pointId=${this.selectedPoint.id}&type=${this.selectedPoint.deleteType || 'Spot'}`)
        .then(async () => {
          this.$snotify.success("removed point");
          this.$refs.confirmDelete.close();
          this.selectedPoint = null;
          this.$emit("reloadAuditLogs");
          await this.getData();
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style>
.pointOverviewList .k-grid table tr:hover td {
  background: rgb(219, 219, 219) !important;
  cursor: pointer !important;
}

.pointOverviewList__sectionCell {
  align-items: center;
  display: inline-flex;
}

.pointOverviewList__sectionItem {
  border-bottom: 1px solid #e5e5e5;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
}

.pointOverviewList__sectionItem:last-child {
  border-bottom: 0;
  margin-bottom: 0;
  padding-bottom: 0;
}
</style>
